import { ToolBase, ToolPointer } from './tool';

/** Umlauf (O): Ziehen dreht die Kamera um den Punkt unter dem Cursor; Umschalt = Schwenken. */
export class OrbitTool extends ToolBase {
  readonly id = 'orbit';
  readonly label = 'Umlauf';
  readonly shortcut = 'O';
  readonly cursor = 'grab';
  readonly hint = 'Ziehen = um den Punkt unter dem Cursor drehen · Umschalt = Schwenken · Mausrad = Zoom';

  private last: { x: number; y: number; pan: boolean } | null = null;

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.last = { x: e.x, y: e.y, pan: e.shift };
    if (e.shift) this.ctx.camera.beginPan(e.ndcX, e.ndcY);
    else this.ctx.camera.beginOrbit(e.ndcX, e.ndcY);
    this.ctx.setViewportCursor('grabbing');
  }

  pointerMove(e: ToolPointer): void {
    if (!this.last) return;
    if (e.shift !== this.last.pan) {
      this.last.pan = e.shift;
      if (e.shift) this.ctx.camera.beginPan(e.ndcX, e.ndcY);
      else this.ctx.camera.beginOrbit(e.ndcX, e.ndcY);
    }
    if (this.last.pan) this.ctx.camera.pan(e.x - this.last.x, e.y - this.last.y);
    else this.ctx.camera.orbit(e.x - this.last.x, e.y - this.last.y);
    this.last.x = e.x;
    this.last.y = e.y;
    this.ctx.requestUpdate();
  }

  pointerUp(_e: ToolPointer): void {
    this.cancel();
  }

  cancel(): void {
    this.last = null;
    this.ctx.camera.endOrbit();
    this.ctx.camera.endPan();
    this.ctx.setViewportCursor(null);
  }

  exit(): void {
    this.cancel();
  }
}

/** Schwenken (H): Ziehen verschiebt die Ansicht — der gegriffene Punkt folgt dem Cursor. */
export class PanTool extends ToolBase {
  readonly id = 'pan';
  readonly label = 'Schwenken';
  readonly shortcut = 'H';
  readonly cursor = 'grab';
  readonly hint = 'Ziehen = Ansicht verschieben · Mausrad = Zoom';

  private last: { x: number; y: number } | null = null;

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.last = { x: e.x, y: e.y };
    this.ctx.camera.beginPan(e.ndcX, e.ndcY);
    this.ctx.setViewportCursor('grabbing');
  }

  pointerMove(e: ToolPointer): void {
    if (!this.last) return;
    this.ctx.camera.pan(e.x - this.last.x, e.y - this.last.y);
    this.last = { x: e.x, y: e.y };
    this.ctx.requestUpdate();
  }

  pointerUp(_e: ToolPointer): void {
    this.cancel();
  }

  cancel(): void {
    this.last = null;
    this.ctx.camera.endPan();
    this.ctx.setViewportCursor(null);
  }

  exit(): void {
    this.cancel();
  }
}

/** Zoom (Z): Ziehen nach oben zoomt auf den angeklickten Punkt zu, nach unten heraus. */
export class ZoomTool extends ToolBase {
  readonly id = 'zoom';
  readonly label = 'Zoom';
  readonly shortcut = 'Z';
  readonly cursor = 'ns-resize';
  readonly hint = 'Ziehen hoch = hinein, runter = heraus (auf den Klickpunkt) · Strg+Umschalt+E = alles anzeigen';

  private drag: { y: number; ndcX: number; ndcY: number } | null = null;

  pointerDown(e: ToolPointer): void {
    if (e.button === 0) this.drag = { y: e.y, ndcX: e.ndcX, ndcY: e.ndcY };
  }

  pointerMove(e: ToolPointer): void {
    if (!this.drag) return;
    const dy = e.y - this.drag.y;
    this.drag.y = e.y;
    if (dy === 0) return;
    this.ctx.camera.zoomAt(Math.exp(dy * 0.01), this.drag.ndcX, this.drag.ndcY);
    this.ctx.requestUpdate();
  }

  pointerUp(_e: ToolPointer): void {
    this.drag = null;
  }

  cancel(): void {
    this.drag = null;
  }
}
